import { useState, useEffect } from 'react'
import { useAuthStore } from '../../store/authStore'
import { supabase } from '../../lib/supabase'
import { useToast } from '../../hooks/useToast'
import { useConfirm } from '../../hooks/useConfirm'
import Toast from '../../components/Toast'
import ConfirmDialog from '../../components/ConfirmDialog'

export default function Security() {
  const { user } = useAuthStore()
  const { toastState, success, error: showError, closeToast } = useToast()
  const { confirm, confirmState, handleConfirm, handleCancel } = useConfirm()
  
  const [loading, setLoading] = useState(false)
  const [showPasswordForm, setShowPasswordForm] = useState(false)
  const [showSessionsModal, setShowSessionsModal] = useState(false)
  const [passwordData, setPasswordData] = useState({
    current: '',
    new: '',
    confirm: ''
  })
  const [userMetadata, setUserMetadata] = useState(null)
  const [sessions, setSessions] = useState([])

  useEffect(() => {
    loadUserData()
    loadSessions()
  }, [user])

  const loadUserData = async () => {
    try {
      const { data } = await supabase
        .from('users')
        .select('*')
        .eq('id', user.id)
        .single()
      
      setUserMetadata(data)
    } catch (err) {
      console.error('Error loading user data:', err)
    }
  }

  const loadSessions = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession()
      if (session) {
        setSessions([{
          id: '1',
          device: getBrowserInfo(),
          location: 'Current Session',
          lastActive: new Date().toLocaleString(),
          current: true
        }])
      }
    } catch (err) {
      console.error('Error loading sessions:', err)
    }
  }

  const getBrowserInfo = () => {
    const ua = navigator.userAgent
    let browser = 'Unknown'
    
    if (ua.includes('Chrome')) browser = 'Chrome'
    else if (ua.includes('Firefox')) browser = 'Firefox'
    else if (ua.includes('Safari')) browser = 'Safari'
    else if (ua.includes('Edge')) browser = 'Edge'
    
    const os = ua.includes('Windows') ? 'Windows' : 
               ua.includes('Mac') ? 'macOS' : 
               ua.includes('Linux') ? 'Linux' : 'Unknown'
    
    return `${browser} on ${os}`
  }

  const handleChangePassword = async (e) => {
    e.preventDefault()
    
    // Validation
    if (!passwordData.new || !passwordData.confirm) {
      showError('Please fill in all fields')
      return
    }

    if (passwordData.new !== passwordData.confirm) {
      showError('New passwords do not match')
      return
    }

    if (passwordData.new.length < 8) {
      showError('Password must be at least 8 characters')
      return
    }

    const confirmed = await confirm({
      title: 'Change Password',
      message: 'Are you sure you want to change your password? You will need to sign in again.',
      confirmText: 'Change Password',
      cancelText: 'Cancel',
      type: 'warning'
    })

    if (!confirmed) return

    setLoading(true)
    
    try {
      const { error } = await supabase.auth.updateUser({
        password: passwordData.new
      })
      
      if (error) throw error
      
      // Update last password change in user metadata
      await supabase
        .from('users')
        .update({
          updated_at: new Date().toISOString()
        })
        .eq('id', user.id)
      
      success('Password changed successfully!')
      setPasswordData({ current: '', new: '', confirm: '' })
      setShowPasswordForm(false)
      
      // Reload user data
      loadUserData()
    } catch (err) {
      console.error('Error changing password:', err)
      showError(err.message || 'Failed to change password')
    } finally {
      setLoading(false)
    }
  }

  const handleSignOutAllSessions = async () => {
    const confirmed = await confirm({
      title: 'Sign Out All Sessions',
      message: 'This will sign you out from all devices. You will need to sign in again.',
      confirmText: 'Sign Out All',
      cancelText: 'Cancel',
      type: 'danger'
    })

    if (!confirmed) return

    setLoading(true)
    
    try {
      await supabase.auth.signOut()
      success('Signed out from all sessions')
      window.location.href = '/#/login'
    } catch (err) {
      console.error('Error signing out:', err)
      showError('Failed to sign out')
    } finally {
      setLoading(false)
    }
  }

  const getPasswordAge = () => {
    if (!userMetadata?.updated_at) return 'Never changed'
    
    const lastUpdate = new Date(userMetadata.updated_at)
    const now = new Date()
    const diffDays = Math.floor((now - lastUpdate) / (1000 * 60 * 60 * 24))
    
    if (diffDays === 0) return 'Changed today'
    if (diffDays === 1) return 'Changed yesterday'
    return `Changed ${diffDays} days ago`
  }

  return (
    <div className="p-8 max-w-7xl mx-auto w-full">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-text-light dark:text-white mb-2">Security</h1>
        <p className="text-text-muted-light dark:text-text-muted-dark">
          Manage your security settings and preferences
        </p>
      </div>

      <div className="space-y-6">
        {/* Account Security Overview */}
        <div className="bg-surface-light dark:bg-surface-dark rounded-2xl shadow-soft border border-border-light dark:border-border-dark p-6">
          <div className="flex items-center gap-4 mb-6">
            <div className="w-12 h-12 rounded-lg bg-green-50 dark:bg-green-500/10 flex items-center justify-center">
              <span className="material-symbols-outlined text-green-500 dark:text-green-400 text-2xl">shield</span>
            </div>
            <div>
              <h2 className="text-lg font-bold text-text-light dark:text-white">Account Security</h2>
              <p className="text-sm text-text-muted-light dark:text-text-muted-dark">
                Your account is protected
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Email Info */}
            <div className="flex items-center gap-3 p-4 bg-gray-50 dark:bg-white/5 rounded-xl border border-transparent dark:border-border-dark">
              <span className="material-symbols-outlined text-primary">email</span>
              <div>
                <p className="font-medium text-sm text-text-light dark:text-white">Email</p>
                <p className="text-xs text-text-muted-light dark:text-text-muted-dark">{user?.email}</p>
              </div>
            </div>

            {/* Account Created */}
            <div className="flex items-center gap-3 p-4 bg-gray-50 dark:bg-white/5 rounded-xl border border-transparent dark:border-border-dark">
              <span className="material-symbols-outlined text-primary">history</span>
              <div>
                <p className="font-medium text-sm text-text-light dark:text-white">Account Created</p>
                <p className="text-xs text-text-muted-light dark:text-text-muted-dark">
                  {userMetadata?.created_at ? new Date(userMetadata.created_at).toLocaleDateString() : 'N/A'}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Password Management */}
        <div className="bg-surface-light dark:bg-surface-dark rounded-2xl shadow-soft border border-border-light dark:border-border-dark p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <span className="material-symbols-outlined text-text-muted-light dark:text-text-muted-dark">lock</span>
              <div>
                <p className="font-medium text-sm text-text-light dark:text-white">Password</p>
                <p className="text-xs text-text-muted-light dark:text-text-muted-dark">{getPasswordAge()}</p>
              </div>
            </div>
            <button 
              onClick={() => setShowPasswordForm(!showPasswordForm)}
              className="text-primary hover:text-primary-hover text-sm font-medium transition-colors"
            >
              {showPasswordForm ? 'Cancel' : 'Change'}
            </button>
          </div>

          {showPasswordForm && (
            <form onSubmit={handleChangePassword} className="mt-4 space-y-4 pt-4 border-t border-border-light dark:border-border-dark">
              <div>
                <label className="block text-sm font-medium mb-2 text-text-light dark:text-white">
                  New Password
                </label>
                <input
                  type="password"
                  value={passwordData.new}
                  onChange={(e) => setPasswordData({ ...passwordData, new: e.target.value })}
                  className="w-full px-4 py-2.5 rounded-xl border border-border-light dark:border-border-dark bg-gray-50 dark:bg-gray-800 text-text-light dark:text-white outline-none focus:ring-2 focus:ring-primary transition-all"
                  placeholder="Enter new password"
                  minLength={8}
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-2 text-text-light dark:text-white">
                  Confirm New Password
                </label>
                <input
                  type="password"
                  value={passwordData.confirm}
                  onChange={(e) => setPasswordData({ ...passwordData, confirm: e.target.value })}
                  className="w-full px-4 py-2.5 rounded-xl border border-border-light dark:border-border-dark bg-gray-50 dark:bg-gray-800 text-text-light dark:text-white outline-none focus:ring-2 focus:ring-primary transition-all"
                  placeholder="Confirm new password"
                  minLength={8}
                  required
                />
              </div>

              <div className="flex gap-3">
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 bg-primary hover:bg-primary-hover text-white px-6 py-2.5 rounded-xl font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Updating...' : 'Update Password'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowPasswordForm(false)
                    setPasswordData({ current: '', new: '', confirm: '' })
                  }}
                  className="px-6 py-2.5 rounded-xl border border-border-light dark:border-border-dark hover:bg-gray-50 dark:hover:bg-white/5 transition-all"
                >
                  Cancel
                </button>
              </div>
            </form>
          )}
        </div>

        {/* Active Sessions */}
        <div className="bg-surface-light dark:bg-surface-dark rounded-2xl shadow-soft border border-border-light dark:border-border-dark p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <span className="material-symbols-outlined text-text-muted-light dark:text-text-muted-dark">devices</span>
              <div>
                <p className="font-medium text-sm text-text-light dark:text-white">Active Sessions</p>
                <p className="text-xs text-text-muted-light dark:text-text-muted-dark">{sessions.length} active session(s)</p>
              </div>
            </div>
            <button 
              onClick={() => setShowSessionsModal(!showSessionsModal)}
              className="text-primary hover:text-primary-hover text-sm font-medium transition-colors"
            >
              {showSessionsModal ? 'Hide' : 'Manage'}
            </button>
          </div>

          {showSessionsModal && (
            <div className="mt-4 pt-4 border-t border-border-light dark:border-border-dark space-y-3">
              {sessions.map((session) => (
                <div 
                  key={session.id}
                  className="flex items-center justify-between p-4 bg-gray-50 dark:bg-white/5 rounded-xl"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                      <span className="material-symbols-outlined text-primary text-xl">
                        {session.current ? 'computer' : 'phone_iphone'}
                      </span>
                    </div>
                    <div>
                      <p className="font-medium text-sm text-text-light dark:text-white">
                        {session.device}
                        {session.current && (
                          <span className="ml-2 text-xs bg-green-100 dark:bg-green-900/20 text-green-600 dark:text-green-400 px-2 py-0.5 rounded-full">
                            Current
                          </span>
                        )}
                      </p>
                      <p className="text-xs text-text-muted-light dark:text-text-muted-dark">
                        {session.location} • {session.lastActive}
                      </p>
                    </div>
                  </div>
                </div>
              ))}

              <button
                onClick={handleSignOutAllSessions}
                disabled={loading}
                className="w-full mt-4 px-4 py-2.5 rounded-xl border border-red-500 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/10 font-medium transition-all disabled:opacity-50"
              >
                Sign Out All Sessions
              </button>
            </div>
          )}
        </div>

        {/* Security Recommendations */}
        <div className="bg-gradient-to-r from-indigo-50 to-purple-50 dark:from-indigo-900/20 dark:to-purple-900/20 rounded-2xl p-6 border border-indigo-200 dark:border-indigo-800">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-lg bg-indigo-500/10 flex items-center justify-center shrink-0">
              <span className="material-symbols-outlined text-indigo-600 dark:text-indigo-400">tips_and_updates</span>
            </div>
            <div>
              <h3 className="font-semibold text-text-light dark:text-white mb-2">Security Tips</h3>
              <ul className="space-y-2 text-sm text-text-muted-light dark:text-text-muted-dark">
                <li className="flex items-start gap-2">
                  <span className="text-indigo-600 dark:text-indigo-400 mt-0.5">•</span>
                  <span>Use a strong, unique password with at least 8 characters</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-indigo-600 dark:text-indigo-400 mt-0.5">•</span>
                  <span>Change your password regularly (every 90 days recommended)</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-indigo-600 dark:text-indigo-400 mt-0.5">•</span>
                  <span>Never share your password with anyone</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-indigo-600 dark:text-indigo-400 mt-0.5">•</span>
                  <span>Sign out when using shared or public devices</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Toast Component */}
      <Toast
        message={toastState.message}
        type={toastState.type}
        isOpen={toastState.isOpen}
        onClose={closeToast}
        duration={toastState.duration}
      />

      {/* Confirm Dialog */}
      <ConfirmDialog
        isOpen={confirmState.isOpen}
        title={confirmState.title}
        message={confirmState.message}
        type={confirmState.type}
        confirmText={confirmState.confirmText}
        cancelText={confirmState.cancelText}
        onConfirm={handleConfirm}
        onClose={handleCancel}
      />
    </div>
  )
}
